import { create } from 'zustand';
import chatApi from '../services/chatApi';
import chatWebsocket from '../services/chatWebsocket';
import { loadChatCache, updateChatCache } from '../services/chatCache';
import {
  applyMessageToDialogs,
  computeUnreadTotal,
  dialogKey,
  sortDialogs,
} from '../utils/chatDialogUtils';

let wsAttached = false;
let syncTimer = null;
let visibilityAttached = false;
const receivedIds = new Set();

function scheduleDialogsSync(get) {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    get().fetchDialogs({ silent: true });
  }, 600);
}

function attachWebSocketHandlers(get, set) {
  if (wsAttached) return;
  wsAttached = true;

  chatWebsocket.on('new_message', (data) => {
    const message = data?.message;
    if (!message) return;

    const msgId = message.id || message.messageId;
    if (msgId) {
      if (receivedIds.has(msgId)) return;
      receivedIds.add(msgId);
      if (receivedIds.size > 1000) {
        const ids = Array.from(receivedIds);
        receivedIds.clear();
        ids.slice(-500).forEach((id) => receivedIds.add(id));
      }
    }

    const { userId, activeDialogKey, dialogs } = get();
    if (!userId) return;

    const { dialogs: next, needsSync } = applyMessageToDialogs(dialogs, message, {
      activeDialogKey,
      myUserId: userId,
    });

    const unreadTotal = computeUnreadTotal(next);
    set({ dialogs: next, unreadTotal });
    updateChatCache(userId, { dialogs: next });

    if (needsSync) scheduleDialogsSync(get);
  });

  chatWebsocket.on('dialog_deleted', () => {
    scheduleDialogsSync(get);
  });

  chatWebsocket.on('message_deleted', () => {
    scheduleDialogsSync(get);
  });

  chatWebsocket.on('message_edited', () => {
    scheduleDialogsSync(get);
  });
}

function attachVisibilityRefresh(get) {
  if (visibilityAttached || typeof document === 'undefined') return;
  visibilityAttached = true;
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && get().userId) {
      get().fetchDialogs({ silent: true });
    }
  });
}

const useChatStore = create((set, get) => ({
  userId: null,
  dialogs: [],
  groups: [],
  channels: [],
  users: [],
  stickerPacks: [],
  activeDialogKey: null,
  unreadTotal: 0,
  dialogsLoading: false,
  dialogsRefreshing: false,
  usersLoading: false,
  lastDialogsFetch: 0,
  sessionReady: false,

  setActiveDialogKey: (key) => set({ activeDialogKey: key ?? null }),

  markDialogRead: (type, id) => {
    const { dialogs, userId } = get();
    const next = dialogs.map((d) =>
      d.type === type && String(d.id) === String(id)
        ? { ...d, unreadCount: 0 }
        : d
    );
    const unreadTotal = computeUnreadTotal(next);
    set({ dialogs: next, unreadTotal });
    if (userId) updateChatCache(userId, { dialogs: next });
  },

  /** Оптимистично поднять диалог при исходящем сообщении. */
  bumpDialogOnOutgoing: (dialog, { text, messageType = 'text' } = {}) => {
    const { dialogs, userId, activeDialogKey } = get();
    if (!dialog) return;
    const key = dialogKey(dialog);
    const time = new Date().toISOString();
    const preview = messageType === 'image'
      ? '📷 Фото'
      : messageType === 'audio'
        ? '🎵 Голосовое'
        : messageType === 'sticker'
          ? '🎨 Стикер'
          : (text || '').substring(0, 200);

    let found = false;
    const updated = dialogs.map((d) => {
      if (dialogKey(d) !== key) return d;
      found = true;
      return {
        ...d,
        lastMessage: preview,
        lastMessageType: messageType,
        lastMessageTime: time,
        unreadCount: 0,
      };
    });

    const next = found ? sortDialogs(updated) : dialogs;
    set({ dialogs: next, unreadTotal: computeUnreadTotal(next) });
    if (userId) updateChatCache(userId, { dialogs: next });
    if (activeDialogKey !== key) {
      set({ activeDialogKey: key });
    }
  },

  removeDialog: (type, id) => {
    const { dialogs, userId } = get();
    const next = dialogs.filter(
      (d) => !(d.type === type && String(d.id) === String(id))
    );
    set({ dialogs: next, unreadTotal: computeUnreadTotal(next) });
    if (userId) updateChatCache(userId, { dialogs: next });
  },

  hydrateFromCache: (userId) => {
    const cached = loadChatCache(userId);
    if (!cached) return false;
    set({
      dialogs: cached.dialogs ?? [],
      groups: cached.groups ?? [],
      channels: cached.channels ?? [],
      users: cached.users ?? [],
      stickerPacks: cached.stickerPacks ?? [],
      unreadTotal: computeUnreadTotal(cached.dialogs ?? []),
    });
    return Boolean(cached.dialogs?.length);
  },

  initSession: (userId, token) => {
    if (!userId || !token) return;

    const prevUserId = get().userId;
    if (prevUserId !== userId) {
      get().hydrateFromCache(userId);
      set({ userId, sessionReady: false });
    }

    chatWebsocket.setToken(token);
    chatWebsocket.connect();
    attachWebSocketHandlers(get, set);
    attachVisibilityRefresh(get);

    const hasDialogs = get().dialogs.length > 0;
    // Все fetch — fire-and-forget с .catch() для предотвращения unhandled rejection
    // на iOS (WebKit обрабатывает их жёстче и может завершить страницу).
    get().fetchDialogs({ silent: hasDialogs }).catch((err) => {
      console.warn('[useChatStore] fetchDialogs error (silent):', err?.message);
    });
    get().fetchGroups({ silent: true }).catch((err) => {
      console.warn('[useChatStore] fetchGroups error (silent):', err?.message);
    });
    get().fetchChannels({ silent: true }).catch((err) => {
      console.warn('[useChatStore] fetchChannels error (silent):', err?.message);
    });

    set({ sessionReady: true });
  },

  /** При открытии страницы чатов — всегда подтягиваем свежие данные с сервера. */
  revalidateOnChatOpen: () => {
    const { userId, dialogs } = get();
    if (!userId) return;
    get().fetchDialogs({ silent: dialogs.length > 0, priority: true });
  },

  fetchDialogs: async ({ silent = false, priority = false } = {}) => {
    const { userId, dialogs } = get();
    if (!userId) return;

    const showBlockingLoader = !silent && dialogs.length === 0;
    if (showBlockingLoader) set({ dialogsLoading: true });
    else set({ dialogsRefreshing: true });

    try {
      const list = await chatApi.getDialogs();
      const unreadTotal = computeUnreadTotal(list);
      set({
        dialogs: list,
        unreadTotal,
        dialogsLoading: false,
        dialogsRefreshing: false,
        lastDialogsFetch: Date.now(),
      });
      updateChatCache(userId, { dialogs: list });
    } catch (err) {
      console.error('[useChatStore] fetchDialogs failed:', err?.message);
      set({ dialogsLoading: false, dialogsRefreshing: false });
      if (!silent && !priority) throw err;
    }
  },

  fetchGroups: async ({ silent = true } = {}) => {
    const { userId } = get();
    if (!userId) return;
    try {
      const groupList = await chatApi.getUserGroups();
      set({ groups: groupList });
      updateChatCache(userId, { groups: groupList });
    } catch (err) {
      if (!silent) console.error('[useChatStore] fetchGroups failed:', err?.message);
    }
  },

  fetchChannels: async ({ silent = true } = {}) => {
    const { userId } = get();
    if (!userId) return;
    try {
      const channelList = await chatApi.getUserChannels();
      set({ channels: channelList });
      updateChatCache(userId, { channels: channelList });
    } catch (err) {
      if (!silent) console.error('[useChatStore] fetchChannels failed:', err?.message);
    }
  },

  fetchUsers: async ({ force = false } = {}) => {
    const { userId, usersLoading, users } = get();
    if (!userId || usersLoading) return;
    if (!force && users.length > 0) return;
    set({ usersLoading: true });
    try {
      const userList = await chatApi.getUsers();
      set({ users: userList, usersLoading: false });
      updateChatCache(userId, { users: userList });
    } catch (err) {
      console.error('[useChatStore] fetchUsers failed:', err?.message);
      set({ usersLoading: false });
    }
  },

  fetchStickerPacks: async () => {
    const { userId, stickerPacks } = get();
    if (!userId || stickerPacks.length > 0) return;
    try {
      const { packs } = await chatApi.getStickerPacks();
      const list = packs || [];
      set({ stickerPacks: list });
      updateChatCache(userId, { stickerPacks: list });
    } catch (err) {
      console.warn('[useChatStore] fetchStickerPacks failed:', err?.message);
    }
  },

  reset: () => {
    if (syncTimer) clearTimeout(syncTimer);
    set({
      userId: null,
      dialogs: [],
      groups: [],
      channels: [],
      users: [],
      stickerPacks: [],
      activeDialogKey: null,
      unreadTotal: 0,
      dialogsLoading: false,
      dialogsRefreshing: false,
      usersLoading: false,
      lastDialogsFetch: 0,
      sessionReady: false,
    });
  },
}));

export default useChatStore;
